(() => {
  const SECTION_CLASS = "story-showcase-section";
  const DESKTOP_QUERY = "(min-width: 990px)";
  const instances = new WeakMap();

  // Design row at scale 1: tiles 200 + 288 + 500 + 288 + 200 plus four 24px
  // gaps. Scaled down together when the viewport is too narrow, so the row
  // keeps its proportions instead of clipping at the edges.
  const COMPOSITION_WIDTH = 1572;
  const COMPOSITION_HEIGHT = 667;
  const COMPOSITION_MARGIN = 80;
  const CENTER_WIDTH = 500;

  // Below the desktop breakpoint only the centre and the inner pair are shown,
  // and that pair is meant to run off both edges, so the scale comes from the
  // centre tile's share of the viewport instead of the whole row's width.
  const CENTER_SHARE = 0.62;

  // Progress at which the story copy starts to fade in. Matches the centre image
  // covering ~70% of the viewport width; see _storyStart().
  const STORY_COVERAGE = 0.7;

  // Floor for that reveal, matching the progress at which the side tiles have
  // finished fading out (the opacity ramp in the section SCSS). An arrangement
  // whose centre already covers most of the viewport hits 70% coverage almost
  // immediately, and without the floor the copy would fade in while the side
  // tiles are still on screen.
  const STORY_MIN_START = 0.72;

  // Progress at which the story copy is fully opaque — the end of the reveal
  // ramp in the section SCSS. Tabbing into the copy scrolls the timeline here so
  // the focused element is actually on screen.
  const STORY_FULL = 0.92;

  class StoryShowcase {
    constructor(root) {
      this.root = root;
      this.stage = root.querySelector(".js-story-showcase-stage");
      this.row = root.querySelector(".js-story-showcase-row");
      this.story = root.querySelector(".js-story-showcase-story");
      // Only counted as a sanity check — the row's tiles carry their own
      // position classes and image sizes from Liquid.
      this.tiles = this.row
        ? this.row.querySelectorAll(".js-story-showcase-tile")
        : [];

      if (!this.stage || !this.row || this.tiles.length === 0) return;

      this.desktop = window.matchMedia(DESKTOP_QUERY);
      this.reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.raf = null;
      this.observer = null;
      this.inView = false;
      this.storyVisible = null;
      this.storyStart = STORY_MIN_START;

      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onStoryFocus = this._onStoryFocus.bind(this);

      this._bind();
      this._update();
    }

    _bind() {
      this.observer = new IntersectionObserver(
        (entries) => {
          this.inView = entries.some((e) => e.isIntersecting);
          if (this.inView) this._requestUpdate();
        },
        { threshold: [0, 0.01, 1] }
      );
      this.observer.observe(this.stage);

      window.addEventListener("scroll", this._onScroll, { passive: true });
      window.addEventListener("resize", this._onResize);

      if (this.story) {
        this.story.addEventListener("focusin", this._onStoryFocus);
      }
    }

    // The copy stays in the tab order at every progress, so Tab can land on it
    // before the reveal starts, or after the panel has unpinned past the end of
    // the stage. Either way, run the timeline to the point the copy is opaque
    // and centred rather than leaving focus on a target that isn't on screen.
    _onStoryFocus(event) {
      if (this.reduced.matches) return;

      const vh = document.documentElement.clientHeight;
      const rect = this.stage.getBoundingClientRect();
      const scrollable = rect.height - vh;
      if (scrollable <= 0) return;

      const progress = Math.min(Math.max(-rect.top / scrollable, 0), 1);
      const goal = Math.min(Math.max(this.storyStart, STORY_FULL), 1);
      const box = event.target.getBoundingClientRect();
      if (progress >= goal && box.top >= 0 && box.bottom <= vh) return;

      window.scrollTo({
        top: window.scrollY + rect.top + goal * scrollable,
        behavior: "instant",
      });
      this._update();
    }

    _onScroll() {
      if (this.inView) this._requestUpdate();
    }

    _onResize() {
      this._requestUpdate();
    }

    _requestUpdate() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        this._update();
      });
    }

    // The stage's own width is the true drawing area: 100vw (and
    // documentElement.clientWidth under the theme's `scrollbar-gutter: stable`)
    // both include the scrollbar gutter, which would push the full-bleed centre
    // image off centre and over the edge.
    _viewport() {
      return {
        w: this.stage.clientWidth,
        h: document.documentElement.clientHeight,
      };
    }

    // Uniform scale that fits the arrangement into the viewport.
    _scale(vw, vh) {
      const fitHeight = (vh - COMPOSITION_MARGIN) / COMPOSITION_HEIGHT;

      if (!this.desktop.matches) {
        return Math.min((vw * CENTER_SHARE) / CENTER_WIDTH, fitHeight);
      }

      return Math.min(
        1,
        (vw - COMPOSITION_MARGIN) / COMPOSITION_WIDTH,
        fitHeight
      );
    }

    // Inset the centre image stops short of at full expansion. Read from the CSS
    // token so the value stays defined in one place.
    _finalInset() {
      const raw = getComputedStyle(this.root)
        .getPropertyValue("--story-final-inset")
        .trim();
      const value = parseFloat(raw);
      if (!value) return 0;
      if (!raw.endsWith("rem")) return value;
      const rootSize = parseFloat(
        getComputedStyle(document.documentElement).fontSize
      );
      return value * (rootSize || 10);
    }

    // Progress at which the growing centre image covers STORY_COVERAGE of the
    // viewport width. Derived rather than hardcoded so the reveal keeps hitting
    // the same visual moment at any viewport size, and at either section width.
    _storyStart(vw, scale, inset) {
      const from = CENTER_WIDTH * scale;
      const span = vw - inset * 2 - from;
      if (span <= 0) return STORY_MIN_START;
      const start = (vw * STORY_COVERAGE - from) / span;
      return Math.min(Math.max(start, STORY_MIN_START), 0.95);
    }

    _setStoryVisible(visible) {
      if (this.storyVisible === visible) return;
      this.storyVisible = visible;
      this.root.classList.toggle("story-showcase--story-visible", visible);
    }

    _update() {
      const { w: vw, h: vh } = this._viewport();
      const scale = this._scale(vw, vh);

      this.stage.style.setProperty("--stage-vw", `${vw}px`);
      this.stage.style.setProperty("--stage-vh", `${vh}px`);
      this.stage.style.setProperty("--story-scale", scale.toFixed(4));

      // Reduced motion keeps the measured tile boxes but freezes the timeline,
      // which is what drops the CSS into its static grid with a readable story.
      if (this.reduced.matches) {
        this.stage.style.setProperty("--progress", "0");
        this._setStoryVisible(true);
        return;
      }

      const rect = this.stage.getBoundingClientRect();
      const scrollable = rect.height - vh;
      const progress =
        scrollable <= 0 ? 1 : Math.min(Math.max(-rect.top / scrollable, 0), 1);

      const storyStart = this._storyStart(vw, scale, this._finalInset());
      this.storyStart = storyStart;

      this.stage.style.setProperty("--progress", progress.toFixed(4));
      this.stage.style.setProperty("--story-start", storyStart.toFixed(4));

      // Gate pointer events on the story copy so it can't be clicked while it is
      // still transparent. Focus order is never gated — see _onStoryFocus().
      this._setStoryVisible(progress >= storyStart);
    }

    destroy() {
      if (this.raf) {
        cancelAnimationFrame(this.raf);
        this.raf = null;
      }
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      window.removeEventListener("scroll", this._onScroll);
      window.removeEventListener("resize", this._onResize);
      if (this.story) {
        this.story.removeEventListener("focusin", this._onStoryFocus);
      }
      this.root.classList.remove("story-showcase--story-visible");
    }
  }

  const initSection = (section) => {
    if (!section || !section.classList.contains(SECTION_CLASS)) return;

    const root = section.querySelector(".story-showcase");
    if (!root) return;

    const existing = instances.get(root);
    if (existing) {
      existing.destroy();
      instances.delete(root);
    }
    instances.set(root, new StoryShowcase(root));
  };

  initSection(document.currentScript.parentElement);

  if (!window.__themeStoryShowcaseEvents) {
    window.__themeStoryShowcaseEvents = true;

    document.addEventListener("shopify:section:load", (event) => {
      initSection(event.target);
    });

    document.addEventListener("shopify:section:unload", (event) => {
      event.target.querySelectorAll(".story-showcase").forEach((root) => {
        const existing = instances.get(root);
        if (!existing) return;
        existing.destroy();
        instances.delete(root);
      });
    });
  }
})();
